import { z } from "zod";
import { ADJUSTMENT_TYPE, PAYMENT_MODE } from "../enums.js";
import { booleanFlag, businessDate, dateRange, listQuery, money, objectId, optionalObjectId, positiveMoney, reason as reasonSchema, } from "./common.js";
/**
 * Digital Khata (§11) and Credit (§12).
 *
 * The Khata is a VIEW over the party's ledger account, not a second store. §11's formula
 * — opening + given + taken + adjustments = current balance — is precisely what the ledger
 * already computes. A parallel khata table would produce a second number that drifts from
 * the first, and then nobody could say which one the party actually owes.
 *
 * What this file adds is the vocabulary: LENA HAI / DENA HAI, "given" and "taken", and the
 * aging buckets that turn a balance into a collections worklist.
 */
/* -------------------------------------------------------------------------- */
/* Khata statement                                                            */
/* -------------------------------------------------------------------------- */
export const khataQuerySchema = listQuery.and(dateRange);
/* -------------------------------------------------------------------------- */
/* Adjustments (§25)                                                          */
/* -------------------------------------------------------------------------- */
/**
 * A balance correction.
 *
 * Never a direct write to a balance — an adjustment is a posted, double-entry transaction
 * against a counter account, exactly like every other movement. `reason` is mandatory and
 * lands verbatim in the audit trail, because an unexplained balance change is the single
 * thing §25 and §62 exist to prevent.
 */
export const createAdjustmentSchema = z
    .object({
    date: businessDate,
    adjustmentType: z.nativeEnum(ADJUSTMENT_TYPE),
    /** Exactly one target must be named. */
    partyId: optionalObjectId,
    accountId: optionalObjectId,
    /**
     * Signed. POSITIVE increases the target's balance, NEGATIVE decreases it.
     *
     * For a party that reads in Khata terms: positive means they owe us more.
     */
    amount: money.refine((v) => v !== 0, "An adjustment of zero would change nothing"),
    /**
     * Where the other side lands. Omit to use the suspense account, which is the correct
     * home for a difference that is not yet explained (§23) — it keeps the books balanced
     * while making the unexplained amount impossible to overlook.
     */
    counterAccountId: optionalObjectId,
    reason: reasonSchema,
    notes: z.string().trim().max(1000).optional(),
})
    .refine((v) => Boolean(v.partyId) !== Boolean(v.accountId), {
    message: "Adjust either a party or an account, not both",
    path: ["partyId"],
});
/* -------------------------------------------------------------------------- */
/* Credit and aging (§12)                                                     */
/* -------------------------------------------------------------------------- */
export const creditQuerySchema = listQuery.extend({
    /** Only parties past their limit. */
    overLimit: booleanFlag.optional(),
    /** Only parties with something overdue. */
    overdueOnly: booleanFlag.optional(),
    bucket: z.enum(["current", "b31_60", "b61_90", "b90plus"]).optional(),
});
/* -------------------------------------------------------------------------- */
/* Bachat Khata — savings (§13)                                               */
/* -------------------------------------------------------------------------- */
export const createSavingsAccountSchema = z.object({
    memberName: z.string().trim().min(2, "Member name is required").max(140),
    /** Link to an existing party when the member is already on the books. */
    partyId: optionalObjectId,
    mobile: z
        .union([z.string().trim().regex(/^[6-9]\d{9}$/, "Enter a 10-digit mobile number"), z.literal("")])
        .optional()
        .transform((v) => (v ? v : undefined)),
    /** Annual interest rate in basis points. 6.5% = 650. */
    interestRateBps: z.number().int().min(0).max(10_000).default(0),
    openingBalance: money.default(0).refine((v) => v >= 0, "A savings account cannot open in deficit"),
    openingDate: businessDate.optional(),
    notes: z.string().trim().max(500).optional(),
});
export const savingsTransactionSchema = z.object({
    date: businessDate,
    savingsAccountId: objectId,
    operation: z.enum(["DEPOSIT", "WITHDRAWAL", "INTEREST", "BONUS"]),
    amount: positiveMoney,
    /** Where the cash physically moves. Not needed for INTEREST or BONUS, which are accrued. */
    accountId: optionalObjectId,
    paymentMode: z.nativeEnum(PAYMENT_MODE).optional(),
    referenceNo: z.string().trim().max(80).optional(),
    narration: z.string().trim().max(500).optional(),
});
/* -------------------------------------------------------------------------- */
/* Settlement (§24)                                                           */
/* -------------------------------------------------------------------------- */
export const createSettlementSchema = z
    .object({
    date: businessDate,
    kind: z.enum(["PARTY", "BANK"]),
    partyId: optionalObjectId,
    sourceAccountId: optionalObjectId,
    destinationAccountId: optionalObjectId,
    amount: positiveMoney,
    chargeRuleId: optionalObjectId,
    manualCharge: money.default(0).refine((v) => v >= 0, "A charge cannot be negative"),
    referenceNo: z.string().trim().max(80).optional(),
    narration: z.string().trim().max(1000).optional(),
})
    .superRefine((v, ctx) => {
    if (v.kind === "PARTY" && !v.partyId) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, path: ["partyId"], message: "Choose the party to settle" });
    }
    if (v.kind !== "PARTY" && (!v.sourceAccountId || !v.destinationAccountId)) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ["destinationAccountId"],
            message: "A bank settlement needs both a source and a destination",
        });
    }
});
/* -------------------------------------------------------------------------- */
/* Reconciliation (§23)                                                       */
/* -------------------------------------------------------------------------- */
export const startReconciliationSchema = z.object({
    bankAccountId: objectId,
    from: businessDate,
    to: businessDate,
    /** The closing balance printed on the bank's own statement. */
    statementBalance: money,
});
/** One line lifted from the bank statement, to be matched against the ledger. */
export const statementLineSchema = z.object({
    date: businessDate,
    description: z.string().trim().max(300),
    referenceNo: z.string().trim().max(80).optional(),
    /** Signed: positive is a credit on the bank statement (money in). */
    amount: money.refine((v) => v !== 0, "A statement line of zero is not meaningful"),
});
export const importStatementSchema = z.object({
    lines: z.array(statementLineSchema).min(1, "Add at least one statement line").max(2000),
});
export const matchLineSchema = z.object({
    lineId: objectId,
    ledgerEntryId: objectId.nullable(),
    status: z
        .enum(["MATCHED", "UNMATCHED", "MISSING_IN_SYSTEM", "MISSING_IN_BANK", "DUPLICATE", "NEEDS_REVIEW"])
        .optional(),
});
//# sourceMappingURL=khata.js.map